import React, { useEffect, useState } from "react";
import { adminMembers, painAreas, painAreaEvent } from "../../../services";
import { useTranslation } from "react-i18next";
import { Modal, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import { RootState } from "../../../redux/store";
import SolidButton from "../../../components/solid-btn";
import PainAreaCard from "../../../components/pain-area-card";
import useToast from "../../../hooks/useToast";
import CircleLoader from "../../../components/circle-loader";
import { isMHIPAvailable, isWithinFirstThirtyDays } from "../helpers";
import { useUnleashFlag } from "../../../hooks/useUnleashFlag";
import "./ModalPainAreaRequest.css";

export type TPainArea = {
  id: string;
  img: string;
  img_url: string;
  survey_type: "intake" | "intake1";
  pre_survery_program: null;
  title: string;
  active: boolean;
};

const Body = ({ paList, handleSend, loading, closePainArea }) => {
  const [selectedPA, setSelectedPA] = useState<TPainArea>();

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", marginTop: "20%", marginBottom: "20%" }}>
        <CircleLoader />
      </div>
    );
  }

  return (
    <div>
      <Row xs={2} md={2} lg={3} className="pain-area-request-grid">
        {paList?.map((element, index) => {
          if (element?.id === "other" && closePainArea) return null;
          return (
            (element?.active || element?.id === "prevention") && (
              <PainAreaCard
                element={element}
                index={index}
                handleClick={() => {
                  setSelectedPA(element);
                  setTimeout(() => handleSend(element), 150);
                }}
                selected={selectedPA}
              />
            )
          );
        })}
      </Row>
    </div>
  );
};

export default function ModalPainAreaRequest({ show, setShow }) {
  const { t } = useTranslation();
  const isNewUI = useUnleashFlag("new-ui");
  const history = useHistory();
  const { user } = useSelector((state: RootState) => state.auth);
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const [closePainArea, setClosePainArea] = useState(false);

  const [painAreaList, setPainAreas] = useState<TPainArea[]>([]);

  useEffect(() => {
    const TWELVE_MONTHS_IN_SECONDS = 365 * 24 * 60 * 60;
    const within30Days = isWithinFirstThirtyDays(user.summary);
    const mhipAvailable = isMHIPAvailable(user.summary);
    const getPainAreas = async () => {
      if (isNewUI) setLoading(true);
      try {
        const result = await painAreas.get();
        const pa = result.data.filter((element: TPainArea) => {
          const existingPA = user.summary.painAreaList?.find(e => e.painAreaName === element.id);
          if (element.id === "prevention" && !mhipAvailable) return false;
          if (!isNewUI) {
            if (within30Days && element.id !== "prevention") return false;
            if (!existingPA) return true;
            if (!existingPA.applicationSubmitted) return false;
            return Math.floor(Date.now() / 1000) - existingPA.applicationSubmitted >= TWELVE_MONTHS_IN_SECONDS;
          }
          const eligibleByCooldown =
            !existingPA ||
            (existingPA.applicationSubmitted &&
              Math.floor(Date.now() / 1000) - existingPA.applicationSubmitted >= TWELVE_MONTHS_IN_SECONDS);
          if (!eligibleByCooldown) return false;
          if (within30Days) return element.id === "prevention";
          return true;
        });
        setPainAreas(pa);
      } finally {
        if (isNewUI) setLoading(false);
      }
    };
    getPainAreas();
  }, [isNewUI]);

  useEffect(() => {
    if (show && user?.sub) {
      painAreaEvent.post({ user_id: user.sub });
    }
  }, [show]);

  const handleSend = async _painArea => {
    if (_painArea.id === "other") {
      setClosePainArea(true);
      return;
    }
    if (user?.sub) {
      painAreaEvent.post({ user_id: user.sub, painAreaId: _painArea.id });
    }
    try {
      setLoading(true);
      if (!user?.summary?.mainPainAreaId) {
        throw new Error("Missing mainPainAreaId for pain area change request");
      }
      await adminMembers.put(user.summary.mainPainAreaId, { emailRequest: true, painArea: _painArea.id });
      setSent(true);
    } catch (error) {
      console.error(error);
      toast.alert({
        type: "error",
        title: "Error sending Pain Area change",
        body: "Error sending request, please try again or contact support",
      });
      setLoading(false);
      onHide();
      return;
    }
    const getUserReference = () => user?.email || user?.phone_number;

    history.push(`/survey/${_painArea.id}/${_painArea.survey_type}`, {
      UserSub: user.sub,
      userReference: getUserReference(),
      isEmail: Boolean(user?.email),
      status: "ABANDONED",
      multiplePA: true,
    });
    localStorage.setItem("rkPainAreaSelected", _painArea.id);
  };

  const onHide = () => {
    setShow(false);
    setSent(false);
  };

  const modalTitle = sent
    ? " Got it! "
    : closePainArea
      ? t("pain_area_selection.close_pain_area_title")
      : t("dashboard.select_pain_area");

  return (
    <Modal show={show} onHide={onHide} dialogClassName={isNewUI ? "modal-pain-area-request" : undefined}>
      <Modal.Header closeButton>
        <Modal.Title>{modalTitle}</Modal.Title>
      </Modal.Header>
      <Modal.Body style={isNewUI ? undefined : { maxHeight: "70vh", overflowY: "auto", overflowX: "hidden" }}>
        {sent ? null : <Body loading={loading} paList={painAreaList} handleSend={handleSend} closePainArea={closePainArea} />}
      </Modal.Body>
      {sent ? (
        <Modal.Footer>
          <SolidButton
            onClick={async () => {
              onHide();
            }}
          >
            Close
          </SolidButton>
        </Modal.Footer>
      ) : null}
    </Modal>
  );
}
